import React, { useMemo } from 'react';
import { Calendar, Clock, CreditCard, Crown, Package } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import {
  type ProductGroup,
  type ProductItem,
  resolveTypeAndId,
  toArray,
  formatDate,
  SubListToggle,
} from './PatientProductsDisplay';

// ── Source grouping helpers ──────────────────────────────────────────────────

function getSourceType(item: ProductItem): string {
  if (item.membershipID) return 'membership';
  if (item.paymentID) return 'payment';
  if (item.packageID) return 'package';
  return 'unknown';
}

const SOURCE_CONFIG: Record<string, { label: string; icon: React.ReactNode }> = {
  membership: { label: 'Memberships', icon: <Crown className="h-3.5 w-3.5" /> },
  payment: { label: 'Payments', icon: <CreditCard className="h-3.5 w-3.5" /> },
  package: { label: 'Packages', icon: <Package className="h-3.5 w-3.5" /> },
  unknown: { label: 'Other', icon: <Package className="h-3.5 w-3.5" /> },
};

function isRealProduct(item: ProductItem): boolean {
  return !!resolveTypeAndId(item);
}

interface SourceGroup {
  key: string;
  label: string;
  icon: React.ReactNode;
  totalQty: number;
  availableQty: number;
  usedManualQty: number;
  usedApptQty: number;
  expiredDeletedQty: number;
  allocatedApptQty: number;
}

interface UpcomingGroup {
  addedDate: string | null;
  totalQty: number;
  inAppointmentsQty: number;
  freeQty: number;
}

interface AppointmentEntry {
  scheduledAppointmentsID: number;
  appointmentTypeName: string | null;
  scheduledAppointmentStartTime: string | null;
  usedDate: string | null;
  totalQty: number;
  isUsed: boolean;
  hasUpcoming: boolean;
}

// ── Source badge colours ────────────────────────────────────────────────────

const SOURCE_COLORS: Record<string, { bg: string; text: string }> = {
  membership: { bg: 'bg-purple-100', text: 'text-purple-800' },
  package: { bg: 'bg-amber-100', text: 'text-amber-800' },
  payment: { bg: 'bg-emerald-100', text: 'text-emerald-800' },
  unknown: { bg: 'bg-gray-100', text: 'text-gray-700' },
};

// ── Component ───────────────────────────────────────────────────────────────

export function PatientProductHistoryModal({
  isOpen,
  onClose,
  group,
  productName,
  onOpenAppointment,
}: {
  isOpen: boolean;
  onClose: () => void;
  group: ProductGroup;
  productName: string;
  onOpenAppointment?: (id: number) => void;
}) {
  const upcomingGroups = useMemo<UpcomingGroup[]>(() => {
    const allUpcoming = [
      ...toArray<ProductItem>(group.upcoming),
      ...toArray<ProductItem>(group.appointments).filter((p) => !isRealProduct(p)),
    ];
    if (allUpcoming.length === 0) return [];

    const map = new Map<string, UpcomingGroup>();
    for (const item of allUpcoming) {
      const dateKey = item.addedDate ?? 'unknown';
      const existing = map.get(dateKey);
      const inAppt = !!item.scheduledAppointmentsID;
      if (existing) {
        existing.totalQty += item.quantity;
        if (inAppt) existing.inAppointmentsQty += item.quantity;
        else existing.freeQty += item.quantity;
      } else {
        map.set(dateKey, {
          addedDate: item.addedDate,
          totalQty: item.quantity,
          inAppointmentsQty: inAppt ? item.quantity : 0,
          freeQty: inAppt ? 0 : item.quantity,
        });
      }
    }

    return Array.from(map.values()).sort((a, b) => {
      const da = a.addedDate ?? '';
      const db = b.addedDate ?? '';
      return da.localeCompare(db);
    });
  }, [group.upcoming, group.appointments]);

  const sourceGroups = useMemo<SourceGroup[]>(() => {
    const receivedItems = [
      ...toArray<ProductItem>(group.available),
      ...toArray<ProductItem>(group.appointments),
      ...toArray<ProductItem>(group.unavailable),
    ].filter(isRealProduct);

    const map = new Map<string, SourceGroup>();
    for (const item of receivedItems) {
      const key = getSourceType(item);
      const existing = map.get(key);
      const hasUsedDate = !!item.usedDate && item.usedDate !== '0000-00-00 00:00:00';
      const hasDeleteDate = !!item.deleteDate && item.deleteDate !== '0000-00-00 00:00:00';
      const hasExpireDate = !!item.expireDate && item.expireDate !== '0000-00-00 00:00:00'
        && new Date() > new Date(item.expireDate);
      const isExpiredOrDeleted = (!hasUsedDate) && (hasDeleteDate || hasExpireDate);
      const hasAppt = !!item.scheduledAppointmentsID;
      const isUsedOnAppt = hasUsedDate && hasAppt;
      const isUsedManual = hasUsedDate && !hasAppt;
      const isAllocatedToAppt = hasAppt && !hasUsedDate && !isExpiredOrDeleted;
      const isAvailable = !hasUsedDate && !isExpiredOrDeleted && !isAllocatedToAppt;

      const delta = {
        availableQty: isAvailable ? item.quantity : 0,
        usedApptQty: isUsedOnAppt ? item.quantity : 0,
        usedManualQty: isUsedManual ? item.quantity : 0,
        expiredDeletedQty: isExpiredOrDeleted ? item.quantity : 0,
        allocatedApptQty: isAllocatedToAppt ? item.quantity : 0,
      };

      if (existing) {
        existing.totalQty += item.quantity;
        existing.availableQty += delta.availableQty;
        existing.usedApptQty += delta.usedApptQty;
        existing.usedManualQty += delta.usedManualQty;
        existing.expiredDeletedQty += delta.expiredDeletedQty;
        existing.allocatedApptQty += delta.allocatedApptQty;
      } else {
        const config = SOURCE_CONFIG[key] ?? SOURCE_CONFIG.unknown;
        map.set(key, {
          key,
          label: config.label,
          icon: config.icon,
          totalQty: item.quantity,
          ...delta,
        });
      }
    }

    const order = ['membership', 'package', 'payment', 'unknown'];
    return Array.from(map.values()).sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  }, [group]);

  const appointmentEntries = useMemo<AppointmentEntry[]>(() => {
    const apptLookup = new Map<number, { name: string | null; startTime: string | null }>();
    for (const item of toArray<ProductItem>(group.appointments)) {
      if (!item.scheduledAppointmentsID) continue;
      if (!apptLookup.has(item.scheduledAppointmentsID)) {
        apptLookup.set(item.scheduledAppointmentsID, {
          name: item.appointmentTypeName || null,
          startTime: item.scheduledAppointmentStartTime || null,
        });
      } else {
        const existing = apptLookup.get(item.scheduledAppointmentsID)!;
        if (!existing.name && item.appointmentTypeName) existing.name = item.appointmentTypeName;
        if (!existing.startTime && item.scheduledAppointmentStartTime) existing.startTime = item.scheduledAppointmentStartTime;
      }
    }

    const allItems = [
      ...toArray<ProductItem>(group.appointments),
      ...toArray<ProductItem>(group.unavailable),
    ];

    const map = new Map<number, AppointmentEntry>();
    for (const item of allItems) {
      if (!item.scheduledAppointmentsID) continue;
      const lookup = apptLookup.get(item.scheduledAppointmentsID);
      const itemName = item.appointmentTypeName || lookup?.name || null;
      const itemStartTime = item.scheduledAppointmentStartTime || lookup?.startTime || null;
      const existing = map.get(item.scheduledAppointmentsID);
      const hasUsedDate = !!item.usedDate && item.usedDate !== '0000-00-00 00:00:00';
      const isUpcoming = !isRealProduct(item);
      if (existing) {
        existing.totalQty += item.quantity;
        if (hasUsedDate) existing.isUsed = true;
        if (isUpcoming) existing.hasUpcoming = true;
        if (!existing.scheduledAppointmentStartTime && itemStartTime) {
          existing.scheduledAppointmentStartTime = itemStartTime;
        }
        if (!existing.appointmentTypeName && itemName) {
          existing.appointmentTypeName = itemName;
        }
        if (!existing.usedDate && hasUsedDate) {
          existing.usedDate = item.usedDate;
        }
      } else {
        map.set(item.scheduledAppointmentsID, {
          scheduledAppointmentsID: item.scheduledAppointmentsID,
          appointmentTypeName: itemName,
          scheduledAppointmentStartTime: itemStartTime,
          usedDate: hasUsedDate ? item.usedDate : null,
          totalQty: item.quantity,
          isUsed: hasUsedDate,
          hasUpcoming: isUpcoming,
        });
      }
    }

    return Array.from(map.values()).sort((a, b) => {
      const da = a.scheduledAppointmentStartTime ?? '';
      const db = b.scheduledAppointmentStartTime ?? '';
      return db.localeCompare(da);
    });
  }, [group]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Product History — ${productName}`}
      size="lg"
    >
      {/* ── Product Sources ───────────────────────────────────────────── */}
      <div className="mb-6">
        <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500">
          Product Sources
        </h3>
        {sourceGroups.length === 0 && upcomingGroups.length === 0 ? (
          <p className="text-sm text-slate-400">No source data available.</p>
        ) : (
          <div className="space-y-2">
            {upcomingGroups.map((ug) => (
              <div key={ug.addedDate ?? 'unknown'} className="rounded-lg border border-dashed border-orange-300 bg-orange-50 px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-800">
                    <Clock className="h-3.5 w-3.5" />
                    Upcoming {formatDate(ug.addedDate)}
                  </span>
                  <span className="ml-auto text-xs font-semibold text-orange-700">
                    x{ug.totalQty} expected
                  </span>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-3 text-[11px]">
                  {ug.inAppointmentsQty > 0 && (
                    <span className="text-orange-800">
                      {ug.inAppointmentsQty} in appointments
                    </span>
                  )}
                  {ug.freeQty > 0 && (
                    <span className="text-orange-700">
                      {ug.freeQty} unallocated
                    </span>
                  )}
                </div>
              </div>
            ))}

            {sourceGroups.map((sg) => {
              const colors = SOURCE_COLORS[sg.key] ?? SOURCE_COLORS.unknown;
              return (
                <div key={sg.key} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${colors.bg} ${colors.text}`}>
                      {sg.icon}
                      {sg.label}
                    </span>
                    <span className="ml-auto text-xs font-semibold text-slate-700">
                      x{sg.totalQty} received
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-3 text-[11px]">
                    {sg.availableQty > 0 && (
                      <span className="text-blue-700">
                        {sg.availableQty} available
                      </span>
                    )}
                    {(sg.allocatedApptQty + sg.usedApptQty) > 0 && (
                      <span className="text-green-700">
                        {sg.allocatedApptQty + sg.usedApptQty} in appointments
                      </span>
                    )}
                    {sg.usedManualQty > 0 && (
                      <span className="text-slate-600">
                        {sg.usedManualQty} disbursed manually
                      </span>
                    )}
                    {sg.expiredDeletedQty > 0 && (
                      <span className="text-red-600">
                        {sg.expiredDeletedQty} expired/deleted
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Appointment History ────────────────────────────────────────── */}
      <div>
        <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500">
          Products Used in Appointments
        </h3>
        {appointmentEntries.length === 0 ? (
          <p className="text-sm text-slate-400">No appointments found for this product type.</p>
        ) : (
          <SubListToggle
            title="Appointments"
            count={appointmentEntries.length}
            countColor="text-[#357bb8]"
            defaultOpen
          >
            <div className="max-h-64 space-y-1 overflow-y-auto">
              {appointmentEntries.map((ae) => (
                <div
                  key={ae.scheduledAppointmentsID}
                  className={`flex items-center gap-3 rounded px-2.5 py-1.5 text-xs text-white ${ae.isUsed ? 'bg-[#748094]' : 'bg-[#357bb8]'}`}
                >
                  <Calendar className="h-3 w-3 shrink-0 opacity-70" />
                  <span className="font-medium">
                    {ae.appointmentTypeName || `Appt #${ae.scheduledAppointmentsID}`}
                  </span>
                  <span className="opacity-80">
                    {ae.scheduledAppointmentStartTime
                      ? formatDate(ae.scheduledAppointmentStartTime)
                      : ae.usedDate
                        ? <>Used {formatDate(ae.usedDate)}</>
                        : 'Date pending'}
                  </span>
                  <span className="opacity-70">x{ae.totalQty}</span>
                  <span className="rounded bg-white/20 px-1.5 py-0.5 text-[10px] font-medium">
                    {ae.isUsed ? 'Used' : 'Allocated'}
                  </span>
                  {ae.hasUpcoming && (
                    <span className="rounded bg-orange-400/30 px-1.5 py-0.5 text-[10px] font-medium">
                      Upcoming
                    </span>
                  )}
                  <span className="flex-1" />
                  {onOpenAppointment && (
                    <button
                      type="button"
                      onClick={() => { onClose(); onOpenAppointment(ae.scheduledAppointmentsID); }}
                      className="rounded bg-orange-600 px-2 py-0.5 text-[10px] font-medium text-white hover:bg-orange-700"
                    >
                      View
                    </button>
                  )}
                </div>
              ))}
            </div>
          </SubListToggle>
        )}
      </div>
    </Modal>
  );
}
